/**
 * Runtime loader for a remote UI bundle served same-origin under `/remote/<name>/` (remote-app
 * contract v1). The manifest names an ES module entry exporting
 * `mount(el, options) => unmount`, plus the CSS files it needs; asset paths are relative to the
 * manifest's own directory.
 *
 * CloudFront answers a missing object under `/remote/*` with `index.html` and a 200, so every
 * response is checked for the content it claims to be rather than trusting the status.
 */

export const SUPPORTED_CONTRACT_MAJOR = 1;

export interface RemoteManifest {
  contract: number;
  name: string;
  version: string;
  entry: string;
  css: string[];
  builtAt?: string;
}

export interface RemoteMountOptions {
  basename: string;
  apiBaseUrl: string;
  env: string;
  onSessionExpired?: () => void;
  navigate?: (to: string) => void;
}

export type RemoteUnmount = () => void;

export interface RemoteModule {
  mount: (el: HTMLElement, options: RemoteMountOptions) => RemoteUnmount;
  CONTRACT_VERSION?: number;
}

export type RemoteLoadStage = "manifest" | "css" | "entry" | "mount";

export class RemoteAppLoadError extends Error {
  constructor(public stage: RemoteLoadStage, message: string) {
    super(message);
    this.name = "RemoteAppLoadError";
  }
}

export interface ResolvedRemote {
  manifest: RemoteManifest;
  entryUrl: string;
  cssUrls: string[];
}

const fail = (stage: RemoteLoadStage, message: string): never => {
  throw new RemoteAppLoadError(stage, message);
};

const contractMajor = (contract: unknown): number =>
  typeof contract === "number" || typeof contract === "string"
    ? Math.trunc(Number(contract))
    : NaN;

/** Resolves `path` against the manifest URL and refuses anything outside the manifest's own
 *  directory, so a bad manifest can't point the host at foreign code. */
const resolveAsset = (path: unknown, manifestUrl: URL, ext: string): string => {
  if (typeof path !== "string" || !path.endsWith(ext)) {
    return fail(
      "manifest",
      `asset ${JSON.stringify(path)} is not a ${ext} path`
    );
  }
  const base = new URL(".", manifestUrl);
  const url = new URL(path, base);
  if (url.origin !== base.origin || !url.pathname.startsWith(base.pathname)) {
    return fail("manifest", `asset ${path} resolves outside ${base.pathname}`);
  }
  return url.href;
};

export function parseManifest(text: string, manifestUrl: URL): ResolvedRemote {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    const looksHtml = /^\s*</.test(text);
    return fail(
      "manifest",
      looksHtml
        ? "manifest response is HTML, not JSON (the remote is probably not deployed)"
        : "manifest is not valid JSON"
    );
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return fail("manifest", "manifest is not a JSON object");
  }
  const m = raw as Partial<RemoteManifest>;
  if (contractMajor(m.contract) !== SUPPORTED_CONTRACT_MAJOR) {
    return fail(
      "manifest",
      `unsupported contract ${JSON.stringify(
        m.contract
      )}; this host speaks ${SUPPORTED_CONTRACT_MAJOR}`
    );
  }
  if (m.css !== undefined && !Array.isArray(m.css)) {
    return fail("manifest", "manifest css must be a list");
  }
  return {
    manifest: m as RemoteManifest,
    entryUrl: resolveAsset(m.entry, manifestUrl, ".js"),
    cssUrls: (m.css ?? []).map((c) => resolveAsset(c, manifestUrl, ".css")),
  };
}

export async function fetchManifest(
  manifestPath: string,
  fetchImpl: typeof fetch = fetch
): Promise<ResolvedRemote> {
  const manifestUrl = new URL(manifestPath, window.location.href);
  let res: Response;
  try {
    res = await fetchImpl(manifestUrl.href, {
      cache: "no-store",
      credentials: "same-origin",
    });
  } catch (e) {
    return fail("manifest", `manifest request failed: ${(e as Error).message}`);
  }
  if (!res.ok) {
    return fail("manifest", `manifest request returned ${res.status}`);
  }
  return parseManifest(await res.text(), manifestUrl);
}

const isJavaScript = (contentType: string | null): boolean =>
  /\b(java|ecma)script\b/i.test(contentType ?? "");

/** A HEAD first, so an HTML fallback page fails with a clear reason instead of the browser's
 *  generic module MIME-type error. Versioned assets are long-cached, so the import reuses it. */
export async function checkEntryIsJavaScript(
  entryUrl: string,
  fetchImpl: typeof fetch = fetch
): Promise<void> {
  let res: Response;
  try {
    res = await fetchImpl(entryUrl, {
      method: "HEAD",
      credentials: "same-origin",
    });
  } catch (e) {
    return fail("entry", `entry request failed: ${(e as Error).message}`);
  }
  if (!res.ok) {
    return fail("entry", `entry request returned ${res.status}`);
  }
  const type = res.headers.get("content-type");
  if (!isJavaScript(type)) {
    return fail("entry", `entry is ${type ?? "untyped"}, not JavaScript`);
  }
}

export type ModuleImporter = (url: string) => Promise<unknown>;

// webpackIgnore keeps this a native browser import of a URL webpack never sees at build time.
export const nativeImport: ModuleImporter = (url) =>
  import(/* webpackIgnore: true */ url);

export async function importRemoteModule(
  entryUrl: string,
  importer: ModuleImporter = nativeImport
): Promise<RemoteModule> {
  let mod: Partial<RemoteModule> | undefined;
  try {
    mod = (await importer(entryUrl)) as Partial<RemoteModule>;
  } catch (e) {
    return fail("entry", `entry failed to load: ${(e as Error).message}`);
  }
  if (!mod || typeof mod.mount !== "function") {
    return fail("entry", "entry module does not export mount()");
  }
  if (
    mod.CONTRACT_VERSION !== undefined &&
    contractMajor(mod.CONTRACT_VERSION) !== SUPPORTED_CONTRACT_MAJOR
  ) {
    return fail(
      "entry",
      `entry speaks contract ${mod.CONTRACT_VERSION}; this host speaks ${SUPPORTED_CONTRACT_MAJOR}`
    );
  }
  return mod as RemoteModule;
}

/** Adds a `<link rel="stylesheet">` per URL and resolves once all have loaded. The returned
 *  `remove` takes them out again, so a remote's CSS never outlives its mount. */
export function injectCss(
  urls: string[],
  doc: Document = document
): { ready: Promise<void>; remove: () => void } {
  const links = urls.map((href) => {
    const link = doc.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.remoteApp = "";
    return link;
  });
  const ready = Promise.all(
    links.map(
      (link) =>
        new Promise<void>((resolve, reject) => {
          link.addEventListener("load", () => resolve(), { once: true });
          link.addEventListener(
            "error",
            () =>
              reject(
                new RemoteAppLoadError("css", `stylesheet failed: ${link.href}`)
              ),
            { once: true }
          );
          doc.head.appendChild(link);
        })
    )
  ).then(() => undefined);
  return {
    ready,
    remove: () => links.forEach((link) => link.remove()),
  };
}

export interface LoaderDeps {
  fetchImpl?: typeof fetch;
  importer?: ModuleImporter;
  doc?: Document;
}

export interface LoadedRemote {
  manifest: RemoteManifest;
  module: RemoteModule;
  removeCss: () => void;
}

export interface LoadOptions {
  /** Aborting removes the injected CSS immediately and rejects, even if the import never settles. */
  signal?: AbortSignal;
  /** Retries add a query to the entry URL, since browsers cache a failed module import by URL. */
  attempt?: number;
}

const abortError = () => {
  const error = new Error("remote app load aborted");
  error.name = "AbortError";
  return error;
};

export const withRetryParam = (entryUrl: string, attempt: number): string => {
  if (attempt <= 0) {
    return entryUrl;
  }
  const url = new URL(entryUrl);
  url.searchParams.set("retry", String(attempt));
  return url.href;
};

/** Manifest, then CSS and entry in parallel. On failure or abort nothing is left in the document. */
export async function loadRemoteApp(
  manifestPath: string,
  {
    fetchImpl = fetch,
    importer = nativeImport,
    doc = document,
  }: LoaderDeps = {},
  { signal, attempt = 0 }: LoadOptions = {}
): Promise<LoadedRemote> {
  const { manifest, entryUrl, cssUrls } = await fetchManifest(
    manifestPath,
    fetchImpl
  );
  if (signal?.aborted) {
    throw abortError();
  }
  const css = injectCss(cssUrls, doc);
  const aborted = new Promise<never>((_, reject) =>
    signal?.addEventListener(
      "abort",
      () => {
        css.remove();
        reject(abortError());
      },
      { once: true }
    )
  );
  const url = withRetryParam(entryUrl, attempt);
  try {
    const [module] = await Promise.race([
      Promise.all([
        checkEntryIsJavaScript(url, fetchImpl).then(() =>
          importRemoteModule(url, importer)
        ),
        css.ready,
      ]),
      aborted,
    ]);
    return { manifest, module, removeCss: css.remove };
  } catch (e) {
    css.remove();
    throw e;
  }
}
